import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import '@/styles/dropdown.css'
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { AppDispatch, RootState } from "@/redux"
import { useDispatch, useSelector } from "react-redux"
import { FieldsResponse } from "@/types/WorkflowCreate/fields"
import { TransformationStepType, WorkflowStepType } from "@/constants/workflows/workflowCreate"
import { AddNewFields } from "../TransformationComponents/AddNewFields"
import { Fanout } from "../TransformationComponents/Fanout"
import { FlattenArrayFields } from "../TransformationComponents/FlattenArrayFields"
import { Separator } from "@/components/ui/separator"
import { GroupByAndAggregate } from "../TransformationComponents/GroupBy"
import { MapObjects } from "../TransformationComponents/MapObjects"
import { useRef } from "react"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { TransformationStep, WFCreateStep } from "@/types/WorkflowCreate/workflowCreate"
import { buildTransformationSchema } from "../TransformationComponents/helpers/fields"
import { GroupByRef } from "../TransformationComponents/types/groupByAndAggregate"
import { MapObjectsRef } from "../TransformationComponents/types/mapObjects"
import { FanoutRef } from "../TransformationComponents/types/fanout"
import { FlatteningFieldsRef } from "../TransformationComponents/types/flattenFields"
import { AddNewFieldsRef } from "../TransformationComponents/types/addNewFields"

export const Transformation = ({
  wfStep,
  showTransformationModal,
  setShowTransformationModal,
  allFields,
}: {
  wfStep: WFCreateStep,
  showTransformationModal: boolean,
  setShowTransformationModal: (val: boolean) => void,
  allFields: Record<string, FieldsResponse>
}) => {
  const dispatch: AppDispatch = useDispatch()
  const groupByRef = useRef<GroupByRef>(null);
  const fanoutRef = useRef<FanoutRef>(null);
  const flattenArrayFieldsRef = useRef<FlatteningFieldsRef>(null);
  const mapObjectsRef = useRef<MapObjectsRef>(null);
  const addNewFieldsRef = useRef<AddNewFieldsRef>(null);
  const { templates } = useSelector((state: RootState) => state.transformation)
  const { transformationConfigurations } = useSelector((state: RootState) => state.workflow.create)
  const selectedConfig = transformationConfigurations.find(config => config.taskOrder === wfStep.order && config.stepName === wfStep.action);
  const selected = templates.find(t => t.id === wfStep.action)
  if (!selected) return null;

  const onSubmit = () => {
    let isValid = true;

    const dispatchConfiguration = (config: TransformationStep["configuration"], stepType: TransformationStepType) => {
      const payload: TransformationStep = {
        taskOrder: wfStep.order,
        stepType,
        configuration: config,
        stepName: selected.id // use selectedId (UUID) instead of name to avoid issues with duplicate names
      }

      dispatch(workflowActions.addTransformationConfiguration(payload))
    }

    // Call validation based on transformation type
    switch (selected.stepType) {
      case TransformationStepType.GROUP_AND_AGGREGATE:
        isValid = groupByRef.current?.validate() ?? false;
        if (isValid && groupByRef.current) {
          const data = groupByRef.current.getData();
          dispatch(workflowActions.setStepAvailableFields({
            stepId: WorkflowStepType.TRANSFORMATION,
            order: wfStep.order,
            fields: buildTransformationSchema(
              data,
              TransformationStepType.GROUP_AND_AGGREGATE,
              allFields[`${wfStep.order - 1};${WorkflowStepType.TRANSFORMATION}`] // from previous step)
            ),
          }))
          dispatchConfiguration(data, TransformationStepType.GROUP_AND_AGGREGATE)
        }
        break;
      case TransformationStepType.MAP_OBJECTS:
        isValid = mapObjectsRef.current?.validate() ?? false;
        if (isValid && mapObjectsRef.current) {
          const data = mapObjectsRef.current.getData();
          dispatch(workflowActions.setStepAvailableFields({
            stepId: WorkflowStepType.TRANSFORMATION,
            order: wfStep.order,
            fields: buildTransformationSchema(
              data,
              TransformationStepType.MAP_OBJECTS,
              allFields[`${wfStep.order - 1};${WorkflowStepType.TRANSFORMATION}`] // from previous step
            )
          }))
          dispatchConfiguration(data, TransformationStepType.MAP_OBJECTS)
        }
        break;
      case TransformationStepType.FANOUT:
        isValid = fanoutRef.current?.validate() ?? false;
        if (isValid && fanoutRef.current) {
          const data = fanoutRef.current.getData();
          dispatch(workflowActions.setStepAvailableFields({
            stepId: WorkflowStepType.TRANSFORMATION,
            order: wfStep.order,
            fields: buildTransformationSchema(
              data,
              TransformationStepType.FANOUT,
              allFields[`${wfStep.order - 1};${WorkflowStepType.TRANSFORMATION}`] // from previous step
            ),
          }))
          dispatchConfiguration(data, TransformationStepType.FANOUT)
        }
        break;
      case TransformationStepType.FLATTEN_ARRAY_FIELDS:
        isValid = flattenArrayFieldsRef.current?.validate() ?? false;
        if (isValid && flattenArrayFieldsRef.current) {
          const data = flattenArrayFieldsRef.current.getData();
          dispatch(workflowActions.setStepAvailableFields({
            stepId: WorkflowStepType.TRANSFORMATION,
            order: wfStep.order,
            fields: buildTransformationSchema(
              data,
              TransformationStepType.FLATTEN_ARRAY_FIELDS,
              allFields[`${wfStep.order - 1};${WorkflowStepType.TRANSFORMATION}`] // from previous step
            )
          }))
          dispatchConfiguration(data, TransformationStepType.FLATTEN_ARRAY_FIELDS)
        }
        break;
      case TransformationStepType.ADD_NEW_FIELDS:
        isValid = addNewFieldsRef.current?.validate() ?? false;
        if (isValid && addNewFieldsRef.current) {
          const data = addNewFieldsRef.current.getData();
          dispatch(workflowActions.setStepAvailableFields({
            stepId: WorkflowStepType.TRANSFORMATION,
            order: wfStep.order,
            fields: buildTransformationSchema(
              data,
              TransformationStepType.ADD_NEW_FIELDS,
              allFields[`${wfStep.order - 1};${WorkflowStepType.TRANSFORMATION}`] // from previous step
            )
          }))
          dispatchConfiguration(data, TransformationStepType.ADD_NEW_FIELDS)
        }
        break;
      default:
        break;
    }

    if (!isValid) {
      return;
    }

    setShowTransformationModal(false)
  }

  const renderConfigurationByStepType = () => {
    switch (selected.stepType) {
      case TransformationStepType.GROUP_AND_AGGREGATE:
        return <GroupByAndAggregate
          ref={groupByRef}
          allFields={allFields}
          initialConfiguration={selectedConfig?.configuration}
        />
      case TransformationStepType.MAP_OBJECTS:
        return <MapObjects
          ref={mapObjectsRef}
          allFields={allFields}
          initialConfiguration={selectedConfig?.configuration}
        />
      case TransformationStepType.FANOUT:
        return <Fanout
          ref={fanoutRef}
          initialConfiguration={selectedConfig?.configuration}
        />
      case TransformationStepType.FLATTEN_ARRAY_FIELDS:
        return <FlattenArrayFields
          ref={flattenArrayFieldsRef}
          allFields={allFields}
          initialConfiguration={selectedConfig?.configuration}
        />
      case TransformationStepType.ADD_NEW_FIELDS:
        return <AddNewFields
          ref={addNewFieldsRef}
          allFields={allFields}
          initialConfiguration={selectedConfig?.configuration}
        />
      default:
        return null
    }
  }

  return (
    <AlertDialog
      open={showTransformationModal}
      onOpenChange={(open) => !open && setShowTransformationModal(false)}
    >
      <AlertDialogContent className="max-w-4xl max-h-[700px] min-h-[300px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-6">
            <div className="flex flex-row items-center justify-between">
              <div>
                <h3 className="">{selected?.name}</h3>
                <p className="text-secondary text-left text-sm font-normal">
                  {selected?.description}
                </p>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <Button variant={ButtonVariant.DEFAULT} type="button" onClick={() => onSubmit()}>
                  Update
                </Button>
              </AlertDialogFooter>
            </div>
          </AlertDialogTitle>
        </AlertDialogHeader>
        <div className="maximum-modal-component-height pr-2">
          <Separator className="mb-2" />
          <div className="flex-1 h-full">
            {renderConfigurationByStepType()}
          </div>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  )
}
